// acta_patch_lot3_8_delivery_intervention_docs_v1
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowClockwise,
  CheckCircle,
  Play,
} from "@phosphor-icons/react";
import {
  Link,
  useParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  gmao,
  users as usersApi,
} from "@/api";
import SignaturePad from "@/components/gmao/SignaturePad";
import InterventionPartUsagePanel from "@/components/gmao/InterventionPartUsagePanel";
import WorkReportForm from "@/components/gmao/WorkReportForm";
import InstallationCompletionForm from "@/components/gmao/InstallationCompletionForm";
import DesktopPageHeader from "@/components/layout/DesktopPageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";


const STATUS_LABELS = {
  planned: "Planifiée",
  confirmed: "En attente",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};


const STATUS_CLASSES = {
  planned:
    "border-[var(--do-info)] bg-[var(--do-info-soft)] text-[var(--do-ink)]",
  confirmed:
    "border-[var(--do-data-violet)] bg-[var(--do-info-soft)] text-[var(--do-ink)]",
  in_progress:
    "border-[var(--do-warning)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  completed:
    "border-[var(--do-success)] bg-[var(--do-success-soft)] text-[var(--do-ink)]",
  cancelled:
    "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
};


const TYPE_LABELS = {
  preventive: "Préventive",
  corrective: "Corrective",
  installation: "Installation",
  inspection: "Inspection",
  other: "Autre",
};


const PRIORITY_LABELS = {
  low: "Basse",
  normal: "Normale",
  high: "Haute",
  urgent: "Urgente",
};


const NEXT_STATUS = {
  planned: "confirmed",
  confirmed: "in_progress",
  in_progress: "completed",
};


const ACTION_LABELS = {
  confirmed: "Confirmer",
  in_progress: "Démarrer",
  completed: "Terminer",
};


const HISTORY_LABELS = {
  created: "Intervention créée",
  updated: "Intervention modifiée",
  status_changed: "Statut modifié",
  cancelled: "Intervention annulée",
  technician_assigned: "Technicien affecté",
  technician_unassigned: "Technicien désaffecté",
  work_report_updated: "Compte rendu modifié",
  attachment_added: "Pièce jointe ajoutée",
  attachment_deleted: "Pièce jointe supprimée",
  signature_added: "Signature client ajoutée",
  part_consumed: "Pièce consommée",
  part_returned: "Pièce restituée",
};


const EDITABLE_REPORT_STATUSES = [
  "confirmed",
  "in_progress",
  "completed",
];



const REPORT_AVAILABLE_STATUSES = [
  "confirmed",
  "in_progress",
  "completed",
];


function formatDateTime(value) {
  if (!value) {
    return "Non renseigné";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
}


function formatDuration(minutes) {
  if (
    minutes === null ||
    minutes === undefined ||
    minutes === ""
  ) {
    return "Non renseignée";
  }

  const numeric =
    Number(minutes);

  const hours =
    Math.floor(
      numeric / 60
    );

  const remaining =
    numeric % 60;

  if (!hours) {
    return `${remaining} min`;
  }

  return (
    remaining
      ? `${hours} h ${remaining} min`
      : `${hours} h`
  );
}


function technicianLabel(
  intervention,
  team,
  user
) {
  if (!(intervention?.assignedUserId || intervention?.assignedTechnicianId)) {
    return "Non affectée";
  }

  if (
    user?.role === "technician"
  ) {
    return "Vous";
  }

  const technician =
    team.find(
      (member) =>
        member.id ===
        (intervention.assignedUserId || intervention.assignedTechnicianId)
    );

  return technician
    ? [
        technician.firstName,
        technician.lastName,
      ]
        .filter(Boolean)
        .join(" ")
    : "Intervenant affecté";
}


function nextStepText(intervention) {
  if (!intervention) {
    return "";
  }
  if (intervention.type === "installation" && intervention.installationId) {
    if (intervention.status === "confirmed" || intervention.status === "in_progress") {
      return "Décrivez l'installation puis cliquez sur Terminer l'installation.";
    }
    if (intervention.status === "completed") {
      return intervention.signatureSignedAt
        ? "Installation terminée. Bon signé par le client."
        : "Installation terminée. Envoyez le bon pour signature client.";
    }
  }

  if (intervention.status === "planned") {
    return intervention.assignedUserId || intervention.assignedTechnicianId
      ? "L’intervention est planifiée et doit être confirmée."
      : "Affectez un technicien puis confirmez l’intervention.";
  }

  if (intervention.status === "confirmed") {
    return "L’intervention est prête à être démarrée.";
  }

  if (intervention.status === "in_progress") {
    return "Complétez le compte rendu puis terminez l’intervention.";
  }

  if (intervention.status === "completed") {
    return "L’intervention est terminée. Le bon peut être téléchargé ou archivé.";
  }

  if (intervention.status === "cancelled") {
    return "Cette intervention a été annulée.";
  }

  return "";
}


export default function GmaoInterventionDetail() {
  const {
    interventionId,
  } = useParams();

  const { user } = useAuth();

  const isTechnician =
    user?.role === "technician";

  const canManage = [
    "admin",
    "maintenance_manager",
  ].includes(user?.role);

  const [
    intervention,
    setIntervention,
  ] = useState(null);

  const [machine, setMachine] =
    useState(null);

  const [history, setHistory] =
    useState([]);

  const [
    attachments,
    setAttachments,
  ] = useState([]);

  const [
    attachmentPreviews,
    setAttachmentPreviews,
  ] = useState([]);

  const [team, setTeam] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [
    savingReport,
    setSavingReport,
  ] = useState(false);

  const [
    uploading,
    setUploading,
  ] = useState(false);

  const [
    downloadingReport,
    setDownloadingReport,
  ] = useState(false);

  const [
    archivingReport,
    setArchivingReport,
  ] =
    useState(false);

  const [error, setError] =
    useState("");


  const load = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const interventionData =
          await gmao.getIntervention(
            interventionId
          );

        const [
          machineData,
          historyData,
          attachmentData,
          teamData,
        ] = await Promise.all([
          gmao.getMachine(
            interventionData.machineId
          ),
          gmao.getInterventionHistory(
            interventionId
          ),
          gmao.listInterventionAttachments(
            interventionId
          ),
          canManage
            ? usersApi.list()
            : Promise.resolve([]),
        ]);

        setIntervention(
          interventionData
        );

        setMachine(
          machineData
        );

        setHistory(
          Array.isArray(historyData)
            ? historyData
            : []
        );

        setAttachments(
          Array.isArray(
            attachmentData
          )
            ? attachmentData
            : []
        );

        setTeam(
          Array.isArray(teamData)
            ? teamData
            : []
        );
      } catch (requestError) {
        const message =
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          );

        setError(message);

        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [
      canManage,
      interventionId,
    ]
  );


  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    let disposed = false;

    const urls = [];

    const loadPreviews =
      async () => {
        const previews =
          await Promise.all(
            attachments.map(
              async (
                attachment
              ) => {
                try {
                  const blob =
                    await gmao
                      .getInterventionAttachmentBlob(
                        interventionId,
                        attachment.id
                      );

                  const url =
                    URL.createObjectURL(
                      blob
                    );

                  urls.push(url);

                  return {
                    ...attachment,
                    url,
                  };
                } catch {
                  return {
                    ...attachment,
                    url: "",
                  };
                }
              }
            )
          );

        if (!disposed) {
          setAttachmentPreviews(
            previews
          );
        }
      };

    loadPreviews();

    return () => {
      disposed = true;

      urls.forEach(
        (url) =>
          URL.revokeObjectURL(
            url
          )
      );
    };
  }, [
    attachments,
    interventionId,
  ]);


  const report =
    intervention?.workReport ||
    {};
  const installationWorkflow = intervention?.type === "installation" && Boolean(intervention?.installationId);
  const assignedUserId = intervention?.assignedUserId || intervention?.assignedTechnicianId;
  const mayCompleteInstallation = installationWorkflow
    && ["technician", "maintenance_manager", "admin"].includes(user?.actualRole || user?.role)
    && ((user?.actualRole || user?.role) === "admin" || assignedUserId === user?.id);



  const canWriteWorkOrder =
    useMemo(
      () => {
        if (!intervention) {
          return false;
        }

        const roleAllowed =
          canManage ||
          (
            isTechnician &&
            intervention
              .assignedTechnicianId ===
              user?.id
          );

        return (
          roleAllowed &&
          EDITABLE_REPORT_STATUSES.includes(
            intervention.status
          )
        );
      },
      [
        canManage,
        intervention,
        isTechnician,
        user?.id,
      ]
    );


  const nextStatus =
    intervention
      ? NEXT_STATUS[
          intervention.status
        ]
      : null;


  const canTransition =
    useMemo(
      () => {
        if (
          !intervention ||
          !nextStatus
        ) {
          return false;
        }

        if (canManage) {
          return true;
        }

        return (
          isTechnician &&
          intervention
            .assignedTechnicianId ===
            user?.id &&
          [
            "confirmed",
            "in_progress",
          ].includes(
            intervention.status
          )
        );
      },
      [
        canManage,
        intervention,
        isTechnician,
        nextStatus,
        user?.id,
      ]
    );


  const completeInstallation = async (summary) => {
    if (!mayCompleteInstallation || !intervention) return;
    setBusy(true);
    try {
      await gmao.completeInstallation(intervention.id, summary);
      toast.success("Installation terminée et bon PDF archivé");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Impossible de terminer l'installation.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const [sendingInstallationReport, setSendingInstallationReport] = useState(false);

  const sendInstallationReport = async () => {
    setSendingInstallationReport(true);
    try {
      const result = await gmao.sendInstallationReport(intervention.id);
      toast.success(`Bon envoyé à ${result.sentTo}`);
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Impossible d'envoyer le bon.");
    } finally {
      setSendingInstallationReport(false);
    }
  };

  const transition = async () => {
    if (
      !intervention ||
      !nextStatus
    ) {
      return;
    }

    setBusy(true);

    try {
      await gmao
        .transitionIntervention(
          intervention.id,
          nextStatus
        );

      toast.success(
        `Intervention ${
          STATUS_LABELS[
            nextStatus
          ].toLowerCase()
        }`
      );

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            .response
            ?.data
            ?.detail
        )
      );
    } finally {
      setBusy(false);
    }
  };


  const downloadInterventionReport =
    async () => {
      if (!intervention) {
        return;
      }

      setDownloadingReport(true);

      try {
        const blob =
          await gmao.getInterventionReportPdfBlob(
            intervention.id
          );

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;
        link.download =
          `bon-intervention-${intervention.id}.pdf`;

        document.body.appendChild(
          link
        );

        link.click();
        link.remove();

        URL.revokeObjectURL(
          url
        );

        toast.success(
          "Bon d’intervention téléchargé"
        );
      } catch (requestError) {
        toast.error(
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          )
        );
      } finally {
        setDownloadingReport(false);
      }
    };



  const archiveInterventionReport = async () => {
    if (!intervention?.id) {
      return;
    }

    setArchivingReport(true);

    try {
      const result =
        await gmao.archiveInterventionReport(
          intervention.id
        );

      toast.success(
        result?.alreadyExists
          ? "Bon déjà archivé dans les documents client"
          : "Bon archivé dans les documents client"
      );

      if (result?.document?.id) {
        setIntervention((current) =>
          current
            ? {
                ...current,
                reportDocumentId:
                  result.document.id,
                reportArchivedAt:
                  result.document.reportGeneratedAt ||
                  new Date().toISOString(),
              }
            : current
        );
      }
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
        "Impossible d'archiver le bon dans les documents."
      );
    } finally {
      setArchivingReport(false);
    }
  };


  const saveReport = async (
    payload
  ) => {
    setSavingReport(true);

    try {
      await gmao
        .updateInterventionWorkReport(
          intervention.id,
          payload
        );

      toast.success(
        "Compte rendu enregistré"
      );

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            .response
            ?.data
            ?.detail
        )
      );
    } finally {
      setSavingReport(false);
    }
  };


  const uploadPhotos = async (
    event
  ) => {
    const files =
      Array.from(
        event.target.files ||
        []
      );

    event.target.value = "";

    if (!files.length) {
      return;
    }

    setUploading(true);

    try {
      for (
        const file
        of files
      ) {
        await gmao
          .uploadInterventionAttachment(
            intervention.id,
            file,
            "photo"
          );
      }

      toast.success(
        files.length > 1
          ? "Photos ajoutées"
          : "Photo ajoutée"
      );

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            .response
            ?.data
            ?.detail
        )
      );
    } finally {
      setUploading(false);
    }
  };


  const saveSignature = async (
    file,
    customerName
  ) => {
    setUploading(true);

    try {
      await gmao
        .uploadInterventionAttachment(
          intervention.id,
          file,
          "signature",
          customerName
        );

      toast.success(
        "Signature client enregistrée"
      );

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            .response
            ?.data
            ?.detail
        )
      );
    } finally {
      setUploading(false);
    }
  };


  const removeAttachment =
    async (
      attachment
    ) => {
      setUploading(true);

      try {
        await gmao
          .deleteInterventionAttachment(
            intervention.id,
            attachment.id
          );

        toast.success(
          attachment.kind ===
          "signature"
            ? "Signature supprimée"
            : "Pièce jointe supprimée"
        );

        await load();
      } catch (requestError) {
        toast.error(
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          )
        );
      } finally {
        setUploading(false);
      }
    };


  if (loading) {
    return (
      <div
        className="p-4 sm:p-6 lg:p-8"
        data-testid="gmao-intervention-detail-loading"
      >
        <div className="label-eyebrow lirah-pulse">
          Chargement de l’intervention
        </div>
      </div>
    );
  }


  if (
    error ||
    !intervention
  ) {
    return (
      <div
        className="max-w-3xl p-4 sm:p-6 lg:p-8"
        data-testid="gmao-intervention-detail-error"
      >
        <Link
          to="/app/gmao/interventions"
          className="text-sm font-heading font-semibold text-[var(--do-accent)]"
        >
          Retour aux interventions
        </Link>

        <div className="mt-5 lirah-card p-8">
          <h1 className="font-heading text-xl font-black">
            Intervention indisponible
          </h1>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            {error}
          </p>
        </div>
      </div>
    );
  }


  const signaturePreview =
    attachmentPreviews.find(
      (attachment) =>
        attachment.kind ===
        "signature"
    );

  const photoPreviews =
    attachmentPreviews.filter(
      (attachment) =>
        attachment.kind ===
        "photo"
    );


  return (
    <div
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="gmao-intervention-detail-page"
    >
      <Link
        to="/app/gmao/interventions"
        data-testid="intervention-detail-back"
        className="mb-3 inline-flex text-sm font-heading font-semibold text-[var(--do-accent)] md:mb-4"
      >
        ← Interventions
      </Link>

      <DesktopPageHeader
        eyebrow={`Ordre de travail · ${intervention.id}`}
        title={intervention.title}
        description={[
          TYPE_LABELS[intervention.type]
            || intervention.type,
          intervention.machineName
            || machine?.name,
          intervention.clientName,
          intervention.siteName,
        ]
          .filter(Boolean)
          .join(" · ")}
        testId="gmao-intervention-detail-desktop-header"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {REPORT_AVAILABLE_STATUSES.includes(
              intervention.status
            ) && (!installationWorkflow || intervention.status === "completed") && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadInterventionReport}
                  disabled={
                    busy ||
                    downloadingReport
                  }
                  data-testid="intervention-download-report"
                >
                  {downloadingReport
                    ? "Génération…"
                    : "Télécharger le bon PDF"}
                </Button>

                {!installationWorkflow && <Button
                  type="button"
                  variant="outline"
                  onClick={archiveInterventionReport}
                  disabled={
                    busy ||
                    archivingReport ||
                    downloadingReport
                  }
                  data-testid="intervention-archive-report-document"
                >
                  {archivingReport
                    ? "Archivage…"
                    : intervention?.reportDocumentId
                      ? "Bon déjà archivé"
                      : "Archiver document client"}
                </Button>}
              </>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={load}
              disabled={busy}
              data-testid="refresh-intervention-detail"
            >
              <ArrowClockwise size={16} />
              Actualiser
            </Button>

            {canTransition && !installationWorkflow && (
              <Button
                type="button"
                onClick={transition}
                disabled={busy}
                data-testid="intervention-detail-transition"
              >
                {nextStatus === "completed" ? (
                  <CheckCircle
                    size={17}
                    weight="bold"
                  />
                ) : (
                  <Play
                    size={17}
                    weight="bold"
                  />
                )}

                {ACTION_LABELS[nextStatus]}
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 md:hidden">
        <div className="label-eyebrow">
          Ordre de travail · {intervention.id}
        </div>

        <h1 className="mt-1 font-heading text-xl font-black">
          {intervention.title}
        </h1>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2 py-1 text-[11px] font-heading font-semibold ${
              STATUS_CLASSES[intervention.status] || ""
            }`}
          >
            {STATUS_LABELS[intervention.status]
              || intervention.status}
          </span>

          <span className="rounded-full bg-[var(--do-surface-soft)] px-2 py-1 text-[11px] font-heading font-semibold">
            Priorité{" "}
            {PRIORITY_LABELS[intervention.priority]
              || intervention.priority}
          </span>
        </div>
      </div>

      <section
        className="rounded-[14px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
        data-testid="intervention-next-step"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="label-eyebrow">
              Prochaine étape
            </div>

            <div className="mt-1 font-heading font-bold">
              {nextStepText(intervention)}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span
              className={`rounded-full border px-2.5 py-1 text-xs font-heading font-semibold ${
                STATUS_CLASSES[intervention.status] || ""
              }`}
            >
              {STATUS_LABELS[intervention.status]
                || intervention.status}
            </span>

            {canTransition && !installationWorkflow && (
              <button
                type="button"
                onClick={transition}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50 md:hidden"
              >
                {ACTION_LABELS[nextStatus]}
              </button>
            )}
          </div>
        </div>
      </section>

      <section
        className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="intervention-context"
      >
        <div className="rounded-[12px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
          <div className="label-eyebrow">
            Machine
          </div>

          <Link
            to={`/app/gmao/machines/${encodeURIComponent(
              machine?.id ||
              intervention.machineId
            )}`}
            className="mt-1 block truncate font-heading text-sm font-bold text-[var(--do-accent)]"
          >
            {intervention.machineName
              || machine?.name
              || "Machine"}
          </Link>

          <div className="mt-1 truncate text-[11px] font-mono-stat text-[var(--do-muted)]">
            {[
              intervention.machineModel
                || machine?.model,
              intervention.machineSerialNumber
                || machine?.serialNumber,
            ]
              .filter(Boolean)
              .join(" · ")
              || intervention.machineId}
          </div>
        </div>

        <div className="rounded-[12px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
          <div className="label-eyebrow">
            Client / Site
          </div>

          <div className="mt-1 truncate font-heading text-sm font-bold">
            {intervention.clientName || "—"}
          </div>

          <div className="mt-1 truncate text-[11px] text-[var(--do-muted)]">
            {intervention.siteName
              || intervention.siteAddress
              || "Site non renseigné"}
          </div>
        </div>

        <div className="rounded-[12px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
          <div className="label-eyebrow">
            {intervention.type === "installation" ? "Intervenant" : "Technicien"}
          </div>

          <div className="mt-1 truncate font-heading text-sm font-bold">
            {technicianLabel(
              intervention,
              team,
              user
            )}
          </div>

          <div className="mt-1 text-[11px] text-[var(--do-muted)]">
            {TYPE_LABELS[intervention.type]
              || intervention.type}
          </div>
        </div>

        <div className="rounded-[12px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
          <div className="label-eyebrow">
            Planification
          </div>

          <div className="mt-1 text-sm font-semibold">
            {intervention.scheduledDate && !intervention.scheduledStart
              ? `${intervention.scheduledDate.split("-").reverse().join("/")} · Horaire à définir`
              : formatDateTime(intervention.scheduledStart)}
          </div>

          <div className="mt-1 text-[11px] text-[var(--do-muted)]">
            Durée réelle :{" "}
            {formatDuration(
              report.actualDurationMinutes
            )}
          </div>
        </div>
      </section>

      {!canWriteWorkOrder &&
        intervention.status ===
          "planned" && (
          <div className="mt-5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-ink)]">
            Le compte rendu sera disponible après confirmation de l’intervention.
          </div>
        )}

      {installationWorkflow ? (
        <div className="mt-4">
          <InstallationCompletionForm
            initialValue={report.workPerformed || ""}
            disabled={busy || !mayCompleteInstallation}
            completed={intervention.status === "completed"}
            onComplete={completeInstallation}
          />
          {intervention.status === "completed" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" variant="actaSecondary"
                disabled={downloadingReport} onClick={downloadInterventionReport}>
                Télécharger le bon PDF
              </Button>
              <Button type="button" variant="actaPrimary" data-testid="installation-send-report"
                disabled={sendingInstallationReport} onClick={sendInstallationReport}>
                {sendingInstallationReport ? "Envoi en cours…" : "Envoyer au client pour signature"}
              </Button>
              <p className="text-sm text-[var(--do-muted)]">
                {intervention.signatureSignedAt ? "Bon signé par le client" : "Signature client en attente"}
              </p>
            </div>
          )}
        </div>
      ) : (
      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <section
          className="rounded-[14px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:p-5"
          data-testid="intervention-work-report-section"
        >
          <div className="mb-5">
            <div className="label-eyebrow">
              Intervention
            </div>

            <h2 className="mt-1 font-heading text-xl font-black">
              Compte rendu technique
            </h2>
          </div>

          <WorkReportForm
            report={report}
            editable={
              canWriteWorkOrder
            }
            saving={
              savingReport
            }
            allowFreeTextParts={false}
            onSubmit={
              saveReport
            }
          />

          <div className="mt-6 border-t border-[var(--do-border)] pt-6">
            <InterventionPartUsagePanel
              interventionId={
                intervention.id
              }
              editable={
                canWriteWorkOrder
              }
              onChanged={load}
            />
          </div>
        </section>

        <div className="space-y-5">
          <section className="lirah-card p-5">
            <div className="label-eyebrow">
              Justificatifs
            </div>

            <h2 className="mt-1 font-heading text-lg font-black">
              Photos
            </h2>

            {canWriteWorkOrder && (
              <label className="mt-4 inline-flex cursor-pointer rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  disabled={uploading}
                  onChange={
                    uploadPhotos
                  }
                  data-testid="intervention-photo-input"
                  className="hidden"
                />

                {uploading
                  ? "Envoi…"
                  : "Ajouter des photos"}
              </label>
            )}

            {photoPreviews.length ===
            0 ? (
              <div className="mt-4 text-sm text-[var(--do-muted)]">
                Aucune photo.
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3">
                {photoPreviews.map(
                  (attachment) => (
                    <figure
                      key={
                        attachment.id
                      }
                      className="overflow-hidden rounded-xl border border-[var(--do-border)]"
                    >
                      {attachment.url ? (
                        <img
                          src={
                            attachment.url
                          }
                          alt={
                            attachment.name
                          }
                          className="aspect-square w-full object-cover"
                        />
                      ) : (
                        <div className="flex aspect-square items-center justify-center text-xs text-[var(--do-muted)]">
                          Aperçu indisponible
                        </div>
                      )}

                      <figcaption className="p-2">
                        <div className="truncate text-xs font-semibold">
                          {attachment.name}
                        </div>

                        {canWriteWorkOrder && (
                          <button
                            type="button"
                            onClick={() =>
                              removeAttachment(
                                attachment
                              )
                            }
                            disabled={
                              uploading
                            }
                            className="mt-2 text-xs font-heading font-semibold text-[var(--do-accent)]"
                          >
                            Supprimer
                          </button>
                        )}
                      </figcaption>
                    </figure>
                  )
                )}
              </div>
            )}
          </section>

          <section
            className="lirah-card p-5"
            data-testid="intervention-signature-section"
          >
            <div className="label-eyebrow">
              Validation client
            </div>

            <h2 className="mt-1 font-heading text-lg font-black">
              Signature
            </h2>

            {signaturePreview?.url && (
              <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
                <img
                  src={
                    signaturePreview.url
                  }
                  alt="Signature client"
                  className="max-h-40 w-full object-contain"
                />

                <div className="mt-2 text-xs text-[var(--do-muted)]">
                  Signé le{" "}
                  {formatDateTime(
                    report.signedAt
                  )}
                </div>

                {canWriteWorkOrder && (
                  <button
                    type="button"
                    onClick={() =>
                      removeAttachment(
                        signaturePreview
                      )
                    }
                    disabled={uploading}
                    className="mt-2 text-xs font-heading font-semibold text-[var(--do-accent)]"
                  >
                    Supprimer la signature
                  </button>
                )}
              </div>
            )}

            <div className="mt-4">
              <SignaturePad
                editable={
                  canWriteWorkOrder
                }
                saving={uploading}
                initialCustomerName={
                  report.customerName ||
                  ""
                }
                signedAt={
                  report.signedAt
                }
                onSave={
                  saveSignature
                }
              />
            </div>
          </section>
        </div>
      </div>

      )}

      <section
        className="mt-4 rounded-[14px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:p-5"
        data-testid="intervention-history"
      >
        <div className="label-eyebrow">
          Traçabilité
        </div>

        <h2 className="mt-1 font-heading text-xl font-black">
          Historique
        </h2>

        {history.length === 0 ? (
          <div className="mt-4 text-sm text-[var(--do-muted)]">
            Aucun événement.
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {[...history]
              .reverse()
              .map((event) => (
                <article
                  key={event.id}
                  className="rounded-xl border border-[var(--do-border)] p-4"
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="font-heading font-bold">
                      {HISTORY_LABELS[
                        event.eventType
                      ] ||
                        event.eventType}
                    </div>

                    <time className="text-xs text-[var(--do-muted)]">
                      {formatDateTime(
                        event.createdAt
                      )}
                    </time>
                  </div>

                  {event.details
                    ?.note && (
                    <div className="mt-2 text-sm text-[var(--do-muted)]">
                      {event.details.note}
                    </div>
                  )}

                  {event.details
                    ?.reason && (
                    <div className="mt-2 text-sm text-[var(--do-accent)]">
                      {event.details.reason}
                    </div>
                  )}
                </article>
              ))}
          </div>
        )}
      </section>
    </div>
  );
}
